// LeadDetailPage — /inbox/lead/:id
//
// Phase 4 of Leads Inbox: the detail view for a direct lead.
//
// Sections (top → bottom):
//   1. Header: ← Back to inbox · status pill
//   2. Customer block: name, email, phone, received timestamp
//   3. Their note
//   4. Design preview (thumbnails + Open interactive preview link)
//   5. Private notes textarea (debounced auto-save, 800ms after last keystroke)
//   6. Action row: Reply via email · Mark as booked · Pass · Archive
//
// Behavior:
//   - Auto-marks status='viewed' on mount (idempotent at the hook level).
//   - "Reply via email" opens mailto: AND stamps responded_at + flips
//     status to 'contacted' if currently new/viewed. The user's email
//     client owns the actual conversation thread.
//   - "Mark as booked" opens BookingModal; confirming with the checkbox
//     left checked fires the lead-booking-confirmation Edge Function.
//   - "Pass" and "Archive" are one-click (no confirm) — they're
//     reversible from the inbox filter chips.
//
// Auth gate: this page checks isEntitled("leads") same as the list view.
// Sub.loading prevents a flash of "Inbox is an Artist Pro feature" for
// the first paint while sub data is being fetched.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useLeadsInbox } from "../hooks/useLeadsInbox";
import { useSubscription } from "../hooks/useSubscription";
import BookingModal from "./BookingModal";

// -- Same button-class workaround as InboxPage. The rules we're guarding
//    against are `button { background: #111827 }` in src/styles.css (no
//    !important) and `.tattoo-app button { ... }` in tattoo-ui.css (scoped
//    to the editor, doesn't reach this page). Class specificity already
//    beats element specificity, so the !important markers are technically
//    redundant — kept defensively until a visual sweep confirms removal.
const detailButtonStyles = `
.tt-lead-action-primary {
  background: #a2332b !important;
  color: #ffffff !important;
}
.tt-lead-action-primary:hover:not(:disabled) {
  background: #c2403a !important;
}
.tt-lead-action-success {
  background: #10b981 !important;
  color: #0a0a0a !important;
}
.tt-lead-action-success:hover:not(:disabled) {
  background: #34d399 !important;
}
.tt-lead-action-ghost {
  background: transparent !important;
  color: #d7d7d7 !important;
}
.tt-lead-action-ghost:hover:not(:disabled) {
  background: #1a1a1a !important;
}
.tt-lead-action-danger {
  background: transparent !important;
  color: #f6caca !important;
}
.tt-lead-action-danger:hover:not(:disabled) {
  background: #1a0f0f !important;
}
`;

const STATUS_PILL = {
  new: { label: "New", bg: "#a2332b", fg: "#fff" },
  viewed: { label: "Viewed", bg: "#2a2a2a", fg: "#cfcfcf" },
  contacted: { label: "Contacted", bg: "#1d4ed8", fg: "#fff" },
  booked: { label: "Booked", bg: "#10b981", fg: "#0a0a0a" },
  passed: { label: "Passed", bg: "#3a3a3a", fg: "#a8a8a8" },
  archived: { label: "Archived", bg: "#3a3a3a", fg: "#a8a8a8" },
};

const NOTES_DEBOUNCE_MS = 800;

export default function LeadDetailPage() {
  const navigate = useNavigate();
  const { id: leadId } = useParams();
  const sub = useSubscription();
  const inbox = useLeadsInbox();

  const lead = useMemo(
    () => inbox.direct.find((l) => l.id === leadId) ?? null,
    [inbox.direct, leadId]
  );

  // Auto-mark-as-viewed on mount, exactly once per leadId. Idempotent at
  // the hook level (only flips if status==='new'), but we still gate on
  // a ref so we don't trigger a no-op DB round trip every render.
  const viewedFiredFor = useRef(null);
  useEffect(() => {
    if (!lead) return;
    if (viewedFiredFor.current === lead.id) return;
    viewedFiredFor.current = lead.id;
    inbox.markLeadViewed(lead.id).catch((err) => {
      // The hook already toasts via setError — but we surface a soft
      // toast too because the inbox-level error pane only shows on the
      // list view, not here.
      console.warn("[LeadDetailPage] markLeadViewed failed", err);
    });
  }, [lead, inbox]);

  // --- entitlement gate ---------------------------------------------------
  if (!sub.loading && !sub.isEntitled("leads")) {
    return (
      <PageShell onBack={() => navigate("/account/billing")}>
        <Card>
          <h3 style={emptyTitleStyle}>Inbox is an Artist Pro feature</h3>
          <p style={emptyBodyStyle}>
            Subscribe to Artist Pro (or start the trial) to access the inbox.
          </p>
        </Card>
      </PageShell>
    );
  }

  // --- loading ------------------------------------------------------------
  if (sub.loading || inbox.loading) {
    return (
      <PageShell onBack={() => navigate("/inbox")}>
        <p style={{ color: "#a8a8a8" }}>Loading lead…</p>
      </PageShell>
    );
  }

  // --- inbox load error ---------------------------------------------------
  // The hook surfaces load failures on `inbox.error`. Show that BEFORE the
  // "not found" branch so we don't tell the user the lead doesn't exist
  // when the truth is "we couldn't reach the server".
  if (inbox.error) {
    return (
      <PageShell onBack={() => navigate("/inbox")}>
        <Card>
          <h3 style={emptyTitleStyle}>Couldn't load this lead</h3>
          <p style={emptyBodyStyle}>{inbox.error}</p>
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              className="tt-lead-action-ghost"
              onClick={() => inbox.refresh()}
              style={ghostButtonStyle}
            >
              Try again
            </button>
          </div>
        </Card>
      </PageShell>
    );
  }

  // --- not found ----------------------------------------------------------
  if (!lead) {
    return (
      <PageShell onBack={() => navigate("/inbox")}>
        <Card>
          <h3 style={emptyTitleStyle}>Lead not found</h3>
          <p style={emptyBodyStyle}>
            This lead has been deleted, or you don't have access to it.
          </p>
        </Card>
      </PageShell>
    );
  }

  return (
    <LeadDetailContent
      lead={lead}
      shop={inbox.shop}
      onBack={() => navigate("/inbox")}
      markLeadStatus={inbox.markLeadStatus}
      saveLeadNotes={inbox.saveLeadNotes}
      sendBookingConfirmation={inbox.sendBookingConfirmation}
    />
  );
}

// ---------------------------------------------------------------------------
// Content (separate so all hooks are stable once the lead exists)
// ---------------------------------------------------------------------------

function LeadDetailContent({
  lead,
  shop,
  onBack,
  markLeadStatus,
  saveLeadNotes,
  sendBookingConfirmation,
}) {
  const [notes, setNotes] = useState(lead.artist_notes ?? "");
  const lastSavedRef = useRef(lead.artist_notes ?? "");
  const debounceRef = useRef(null);
  // Track whether the most recent save attempt failed so the inline
  // indicator can swap from "Saving…" to "Save failed". Cleared on the
  // next typing event (which will trigger a fresh save attempt).
  const [saveFailed, setSaveFailed] = useState(false);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [busy, setBusy] = useState(null); // null | "booked" | "pass" | "archive" | "reply"

  // If a different lead arrives (e.g. via in-page nav), sync the textarea.
  // Won't normally fire in v1 since each detail page is its own route, but
  // cheap to keep.
  useEffect(() => {
    setNotes(lead.artist_notes ?? "");
    lastSavedRef.current = lead.artist_notes ?? "";
    setSaveFailed(false);
  }, [lead.id, lead.artist_notes]);

  // Debounced auto-save for notes. We compare against lastSavedRef so
  // typing-and-reverting doesn't fire a write.
  useEffect(() => {
    if (notes === lastSavedRef.current) return;
    // Optimistically clear any prior failure marker as the user keeps
    // typing — the next debounced attempt will re-set it if it fails.
    setSaveFailed(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await saveLeadNotes(lead.id, notes);
        lastSavedRef.current = notes;
        setSaveFailed(false);
      } catch (err) {
        setSaveFailed(true);
        toast.error("Couldn't save notes — keep typing to retry.");
      }
    }, NOTES_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [notes, lead.id, saveLeadNotes]);

  const pill = STATUS_PILL[lead.status] || STATUS_PILL.viewed;
  const customerName = lead.user_name?.trim() || "Anonymous";
  const shopName = shop?.display_name || shop?.name || "Tattune";

  // ---- actions -----------------------------------------------------------

  function handleReplyViaEmail() {
    if (!lead.user_email) {
      toast.error("No email on file for this customer.");
      return;
    }
    const subject = `Re: Your Tattune inquiry`;
    const greetingName = lead.user_name?.trim()?.split(" ")?.[0] || "there";
    const body = [
      `Hi ${greetingName},`,
      ``,
      `[your reply here]`,
      ``,
      `— ${shopName}`,
      `Sent via Tattune`,
    ].join("\n");
    const mailto = `mailto:${encodeURIComponent(
      lead.user_email
    )}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Open the mailto in a real navigation (don't use window.open — some
    // mail clients dislike that). Fire-and-forget; we don't know whether
    // the user actually sends, so we mark "contacted" optimistically.
    window.location.href = mailto;

    // Stamp status as contacted + responded_at, but only if currently
    // new/viewed. Don't overwrite booked/passed/archived just because the
    // artist clicked Reply.
    if (lead.status === "new" || lead.status === "viewed") {
      setBusy("reply");
      markLeadStatus(lead.id, "contacted", { responded: true })
        .catch(() => {
          toast.error("Couldn't update status — refresh to retry.");
        })
        .finally(() => setBusy(null));
    }
  }

  async function handleBookingConfirm({ sendEmail }) {
    setBusy("booked");
    try {
      // Order matters slightly: send email first so a failed email
      // surfaces before we change the status. If the email fails the
      // user can decide whether to still mark booked manually.
      if (sendEmail) {
        try {
          const result = await sendBookingConfirmation(lead.id);
          if (result?.sent === false) {
            toast(
              `Marked as booked. (No confirmation email sent — ${
                result?.reason === "no_resend_key"
                  ? "email service is offline"
                  : "no email on file"
              }.)`,
              { duration: 5000 }
            );
          } else {
            toast.success(
              `Booked! Confirmation sent to ${customerName}.`,
              { duration: 4500 }
            );
          }
        } catch (err) {
          // Email send failed — surface the error but DO still mark the
          // status. The artist's intent was clear and we don't want a
          // transient email failure to leave the lead in limbo.
          toast.error(
            "Booking saved, but the confirmation email failed to send."
          );
        }
      } else {
        toast.success("Marked as booked.");
      }
      await markLeadStatus(lead.id, "booked");
      setBookingOpen(false);
    } catch (err) {
      toast.error("Couldn't update status — try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handlePass() {
    setBusy("pass");
    try {
      await markLeadStatus(lead.id, "passed");
      toast.success("Passed.");
    } catch {
      toast.error("Couldn't update — try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handleArchive() {
    setBusy("archive");
    try {
      await markLeadStatus(lead.id, "archived");
      toast.success("Archived.");
    } catch {
      toast.error("Couldn't update — try again.");
    } finally {
      setBusy(null);
    }
  }

  // ---- render ------------------------------------------------------------

  const previewHref =
    lead.public_slug ? `/lead/${lead.public_slug}` : null;
  const designImages = Array.isArray(lead.design_images)
    ? lead.design_images
    : [];

  return (
    <PageShell onBack={onBack}>
      {/* Header row: customer name + status */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 600,
              margin: 0,
              letterSpacing: "0.01em",
            }}
          >
            {customerName}
          </h1>
          <div style={{ fontSize: 13, color: "#a8a8a8", marginTop: 4 }}>
            Received {formatFullDate(lead.created_at)}
          </div>
        </div>
        <StatusPill spec={pill} />
      </div>

      {/* Contact card */}
      <Card>
        <Row label="Email">
          {lead.user_email ? (
            <a
              href={`mailto:${lead.user_email}`}
              style={{ color: "#9ec5ff", textDecoration: "none" }}
            >
              {lead.user_email}
            </a>
          ) : (
            <Muted>—</Muted>
          )}
        </Row>
        <Row label="Phone">
          {lead.user_phone ? (
            <a
              href={`tel:${lead.user_phone}`}
              style={{ color: "#9ec5ff", textDecoration: "none" }}
            >
              {lead.user_phone}
            </a>
          ) : (
            <Muted>—</Muted>
          )}
        </Row>
      </Card>

      {/* Their note */}
      <SectionHeader>Their note</SectionHeader>
      <Card>
        <p
          style={{
            fontSize: 14,
            color: "#eaeaea",
            lineHeight: 1.55,
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {lead.note?.trim() ? (
            <>“{lead.note.trim()}”</>
          ) : (
            <Muted>No note left.</Muted>
          )}
        </p>
      </Card>

      {/* Design preview */}
      <SectionHeader>Design preview</SectionHeader>
      <Card>
        {designImages.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            {designImages.map((src, i) => (
              <a
                key={i}
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  width: 140,
                  height: 140,
                  borderRadius: 10,
                  background: "#1a1a1a",
                  border: "1px solid #2a2a2a",
                  overflow: "hidden",
                }}
              >
                <img
                  src={src}
                  alt={`Design ${i + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </a>
            ))}
          </div>
        ) : (
          <Muted>No images attached.</Muted>
        )}

        {previewHref && (
          <div style={{ marginTop: 14 }}>
            <a
              href={previewHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                border: "1px solid #2a2a2a",
                borderRadius: 10,
                color: "#d7d7d7",
                fontSize: 13,
                textDecoration: "none",
              }}
            >
              Open full preview ↗
            </a>
          </div>
        )}
      </Card>

      {/* Private notes */}
      <SectionHeader>Your private notes</SectionHeader>
      <p
        style={{
          fontSize: 12,
          color: "#888",
          margin: "0 0 8px",
        }}
      >
        Only you can see these.
      </p>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="e.g. called back, left voicemail. Wants to book early Q2."
        rows={4}
        style={{
          width: "100%",
          padding: "12px 14px",
          background: "#0f0f0f",
          border: "1px solid #1f1f1f",
          borderRadius: 12,
          color: "#eaeaea",
          fontSize: 14,
          resize: "vertical",
          outline: "none",
          fontFamily: "inherit",
          lineHeight: 1.5,
          boxSizing: "border-box",
        }}
      />
      <div
        style={{
          fontSize: 11,
          color: saveFailed ? "#f6caca" : "#666",
          marginTop: 6,
          minHeight: 14,
        }}
      >
        {saveFailed
          ? "Save failed — keep typing to retry"
          : notes !== lastSavedRef.current
          ? "Saving…"
          : "Saved"}
      </div>

      {/* Actions */}
      <SectionHeader>Actions</SectionHeader>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <button
          type="button"
          className="tt-lead-action-primary"
          onClick={handleReplyViaEmail}
          disabled={!!busy}
          style={primaryButtonStyle}
        >
          Reply via email
        </button>

        <button
          type="button"
          className="tt-lead-action-success"
          onClick={() => setBookingOpen(true)}
          disabled={!!busy || lead.status === "booked"}
          style={primaryButtonStyle}
        >
          {lead.status === "booked" ? "Booked ✓" : "Mark as booked"}
        </button>

        <button
          type="button"
          className="tt-lead-action-ghost"
          onClick={handlePass}
          disabled={!!busy || lead.status === "passed"}
          style={ghostButtonStyle}
        >
          {busy === "pass" ? "Passing…" : "Pass"}
        </button>

        <button
          type="button"
          className="tt-lead-action-ghost"
          onClick={handleArchive}
          disabled={!!busy || lead.status === "archived"}
          style={ghostButtonStyle}
        >
          {busy === "archive" ? "Archiving…" : "Archive"}
        </button>
      </div>

      <BookingModal
        open={bookingOpen}
        leadName={lead.user_name?.trim() || "this customer"}
        busy={busy === "booked"}
        onCancel={() => setBookingOpen(false)}
        onConfirm={handleBookingConfirm}
      />
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Layout + atoms (mirrors InboxPage)
// ---------------------------------------------------------------------------

function PageShell({ children, onBack }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0a0a",
        color: "#eaeaea",
        padding: "32px 20px 80px",
      }}
    >
      <style>{detailButtonStyles}</style>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "transparent",
            border: "1px solid #2a2a2a",
            color: "#d7d7d7",
            padding: "6px 12px",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          ← Back to inbox
        </button>
        {children}
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div
      style={{
        background: "#111111",
        border: "1px solid #1f1f1f",
        borderRadius: 14,
        padding: "18px 20px",
      }}
    >
      {children}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h3
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "#888",
        margin: "24px 0 10px",
        fontWeight: 600,
      }}
    >
      {children}
    </h3>
  );
}

function Row({ label, children }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12,
        padding: "6px 0",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#888",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          minWidth: 60,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          color: "#eaeaea",
          textAlign: "right",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function StatusPill({ spec }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        background: spec.bg,
        color: spec.fg,
        whiteSpace: "nowrap",
      }}
    >
      {spec.label}
    </span>
  );
}

function Muted({ children }) {
  return <span style={{ color: "#666" }}>{children}</span>;
}

const primaryButtonStyle = {
  padding: "11px 18px",
  border: "none",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  letterSpacing: "0.02em",
};

const ghostButtonStyle = {
  padding: "10px 16px",
  border: "1px solid #2a2a2a",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const emptyTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  margin: "0 0 8px",
  color: "#eaeaea",
};

const emptyBodyStyle = {
  fontSize: 14,
  color: "#a8a8a8",
  lineHeight: 1.55,
  margin: 0,
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatFullDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
